/**
 * SceneTemplate.tsx — SceneCheck / Image Analysis.
 * Covers: image_insize, irbis_eye.
 *
 * Uses the existing `prepareImageInsizeResults` helper. The payload is the
 * richest of all the workflows: location + confidence, per-person attributes,
 * texts and symbols, threat assessment, entities, and the model's own
 * reasoning under `reflections.analyzedElements`.
 *
 * Everything here is a model inference, and the template says so rather than
 * presenting it as fact.
 */
import * as React from 'react'
import { Box, Typography } from '@mui/material'
import { prepareImageInsizeResults } from '../vendor/searcherService'
import { MapCard } from '../ui/MapCard'
import {
  Chips, Cols, Duo, FieldTable, NoData, RawCard, ResCard, Sech, Stack, Stats, usePhotoLightbox, ValueList, Verdict, useTokens,
} from '../ui/primitives'
import type { TemplateProps } from '../registry'
import { radii } from '../tokens'

const pct = (v: any) => (v == null ? null : `${Math.round(Number(v) * (Number(v) <= 1 ? 100 : 1))}%`)

const PEOPLE_ARROW_SIZE = 30
const PEOPLE_GAP = 10
const PEOPLE_CARD_WIDTH = 240

/**
 * "People in the image" carousel — same round prev/next arrow pattern
 * searcher-v2/parts/InputTypeTiles.tsx uses for step 1's "I have" tile row:
 * a scroll-snapped horizontal track, arrows disabled at each edge, one
 * `scrollBy` page (the track's own `clientWidth`) per click. Rebuilt here
 * rather than imported — InputTypeTiles lives deep in the search-console
 * feature tree and carries drag-to-reorder/localStorage ordering this card
 * has no use for; only the arrow-track shape is worth reusing.
 */
function PeopleCarousel({ people }: { people: any[] }) {
  const tk = useTokens()
  const trackRef = React.useRef<HTMLDivElement | null>(null)
  const [atStart, setAtStart] = React.useState(true)
  const [atEnd, setAtEnd] = React.useState(true)
  // Arrows only appear once the track actually overflows its row — with 1-2
  // people everything already fits, and showing a pair of inert arrows next
  // to cards that can't scroll would be misleading.
  const [canScroll, setCanScroll] = React.useState(false)

  const updateEdges = React.useCallback(() => {
    const el = trackRef.current
    if (!el) return
    setAtStart(el.scrollLeft <= 1)
    setAtEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - 1)
    setCanScroll(el.scrollWidth > el.clientWidth + 1)
  }, [])

  React.useLayoutEffect(() => {
    updateEdges()
  }, [updateEdges, people.length])

  const scrollByPage = (dir: 1 | -1) => {
    const el = trackRef.current
    if (!el) return
    el.scrollBy({ left: dir * el.clientWidth, behavior: 'smooth' })
  }

  const arrowSx = (disabled: boolean) => ({
    flex: 'none' as const,
    width: PEOPLE_ARROW_SIZE,
    height: PEOPLE_ARROW_SIZE,
    borderRadius: '50%',
    border: `1px solid ${tk.border}`,
    background: tk.surface2,
    color: tk.text2,
    display: 'grid',
    placeItems: 'center',
    cursor: disabled ? 'default' : 'pointer',
    opacity: disabled ? 0.4 : 1,
  })

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      {canScroll && (
        <Box
          component="button" type="button" onClick={() => scrollByPage(-1)} disabled={atStart} aria-label="Previous person"
          sx={{ ...arrowSx(atStart), '&:hover': atStart ? {} : { background: tk.surface3, color: tk.text } }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
            <path d="m15 18-6-6 6-6" />
          </svg>
        </Box>
      )}

      <Box
        ref={trackRef}
        onScroll={updateEdges}
        sx={{
          display: 'flex', gap: `${PEOPLE_GAP}px`, overflowX: 'auto', scrollSnapType: 'x mandatory',
          scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' }, flex: 1, minWidth: 0,
        }}
      >
        {people.map((p, i) => (
          <Box
            key={i}
            sx={{
              flex: `0 0 ${PEOPLE_CARD_WIDTH}px`, scrollSnapAlign: 'start', minWidth: 0,
              border: `1px solid ${tk.border}`, borderRadius: `${radii.md}px`, background: tk.surface2, p: '11px',
            }}
          >
            <Typography sx={{ fontSize: 12.5, fontWeight: 700, mb: '7px', color: tk.text }}>Person {i + 1}</Typography>
            <FieldTable
              rows={([
                ['Gender', p.gender],
                ['Age', p.ageRange],
                ['Ethnicity', p.ethnicity],
                ['Identified as', p.identifiedAs],
                ['Clothing', p.clothing],
                ['Accessories', p.accessories],
                ['Tattoos', p.tattoos],
                ['Behaviour', p.behaviorNotes],
                ['Risk', p.riskIndicators],
              ].filter(([, v]) => v && !/^(none|none visible|unclear|n\/a)$/i.test(String(v).trim())) as [string, string][])}
            />
          </Box>
        ))}
      </Box>

      {canScroll && (
        <Box
          component="button" type="button" onClick={() => scrollByPage(1)} disabled={atEnd} aria-label="Next person"
          sx={{ ...arrowSx(atEnd), '&:hover': atEnd ? {} : { background: tk.surface3, color: tk.text } }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
            <path d="m9 18 6-6-6-6" />
          </svg>
        </Box>
      )}
    </Box>
  )
}

export function SceneTemplate({ apiData, mapSlot }: TemplateProps & { mapSlot?: React.ReactNode }) {
  const tk = useTokens()

  const item: any = React.useMemo(() => {
    try {
      const r = prepareImageInsizeResults(apiData as any)
      return Array.isArray(r?.data) && r.data.length ? r.data[0] : null
    } catch {
      return null
    }
  }, [apiData])

  // Everything below through `usePhotoLightbox` must run on every render,
  // `item` present or not — Rules of Hooks forbids calling hooks after an
  // early return, so the `if (!item)` bail-out is deferred until after both
  // hooks have run, and each derives its input with `item?.` so a null item
  // just yields "no coordinates" / "no photo" instead of throwing.
  const loc = item?.location || {}
  const coords = loc.coordinates
  const query = (apiData as any)?.criteria
  const isImage = typeof query === 'string' && query.startsWith('data:image')

  const scenePoint = React.useMemo(() => {
    if (!coords) return null
    const lat = typeof coords.lat === 'string' ? parseFloat(coords.lat) : coords.lat
    const lng = typeof coords.lon === 'string' ? parseFloat(coords.lon) : coords.lon
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null
    return { lat, lng, title: [loc.city, loc.country].filter(Boolean).join(', ') || 'Estimated location', approximate: !!coords.approximate }
  }, [coords, loc.city, loc.country])

  const { openAt, lightbox } = usePhotoLightbox(isImage ? [query] : [])

  if (!item) return <NoData title="No analysis returned" hint="The image could not be analysed. Try a larger or clearer photo." />

  const time = item.time || {}
  const people: any[] = Array.isArray(item.people) ? item.people : []
  const texts = item.textsAndSymbols || {}
  const threat = item.threatAssessment || {}
  const objects = item.objectsAndScene || {}
  const entities: string[] = Array.isArray(item.Entities) ? item.Entities : Array.isArray(item.entities) ? item.entities : []
  const evidence: string[] = Array.isArray(item.locationEvidence) ? item.locationEvidence : []
  const reflections: string[] = Array.isArray(item?.reflections?.analyzedElements) ? item.reflections.analyzedElements : []
  const candidates: string[] = Array.isArray(item.topLocationCandidates) ? item.topLocationCandidates : []
  const alert = String(threat.Alert ?? threat.alert ?? '').toLowerCase()
  const alertTone = alert === 'high' ? 'bad' : alert === 'medium' ? 'warn' : 'ok'

  const hasTexts = Boolean((texts.visibleTexts || []).length + (texts.symbolsOrLogos || []).length + (texts.uniformsOrFlags || []).length)
  const hasPeople = people.length > 0
  const hasInferred = evidence.length > 0 || reflections.length > 0

  // Legacy ImageInsizeCommon.tsx's "Vericity Score" — per-section confidence
  // (0-100, already a percentage, not 0-1 like locationConfidence/confidence
  // above) the model reports for how much it trusts its own location/time/
  // people/etc. read of the photo. Not present on every payload (the sample
  // this template was rebuilt against doesn't include it), so it only
  // renders when at least one section actually has a score.
  const VERICITY_LABELS: Record<string, string> = {
    location: 'Location', time: 'Time', people: 'People', textsAndSymbols: 'Texts & symbols',
    threatAssessment: 'Threat assessment', objectsAndScene: 'Objects & scene', reflections: 'Reasoning',
  }
  const vericity: [string, number][] = Object.entries(item.vericityScore || {})
    .filter((e): e is [string, number] => typeof e[1] === 'number' && e[1] > 0)
  const hasVericity = vericity.length > 0

  return (
    <>
      <ResCard title="Key facts">
        <Stats
          items={[
            { label: 'Location', value: loc.country ?? '—', sub: [candidates[0], loc.regionType].filter(Boolean).join(' · ') },
            { label: 'Location confidence', value: pct(item.locationConfidence) ?? '—', sub: 'model estimate', tone: 'warn' },
            { label: 'Profile confidence', value: pct(item.confidence) ?? '—', sub: 'model estimate', tone: 'ok' },
            { label: 'People', value: people.length },
            { label: 'Threat level', value: threat.Alert ?? threat.alert ?? '—', sub: 'no weapons detected', tone: alertTone as any },
            { label: 'Time', value: time.estimatedTimeOfDay ?? '—', sub: time.season },
          ]}
        />
      </ResCard>

      {/*
        Threat assessment above Scene on the left — the verdict reads before
        the photo does, not after; Locations/Map/Objects (and Texts, when
        present) stacked on the right so their combined height lands close
        to the left column's rather than any one of them being stretched
        thin to match it. Stack's own last-child-grows rule still covers the
        (rare) case where a stack ends up shorter than its neighbour.
      */}
      <Cols n={2}>
        <Stack>
          <ResCard title="Threat assessment" tone={alertTone === 'ok' ? 'good' : 'accent'}>
            <Verdict
              tone={alertTone as any}
              title={String(threat.Alert ?? threat.alert ?? 'Unknown')}
              subtitle={
                [
                  `${(threat.weapons || []).length} weapons`,
                  `${(threat.suspiciousObjects || []).length} suspicious objects`,
                  `${(threat.violenceIndicators || []).length} violence indicators`,
                ].join(' · ')
              }
              icon={<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 3 4 6v6c0 5 3.4 9.2 8 10 4.6-.8 8-5 8-10V6z" /><path d="m9 12 2 2 4-4" /></svg>}
            />
          </ResCard>

          <ResCard title="Scene">
            <Box
              sx={{
                minHeight: 220, maxHeight: 480, borderRadius: `${radii.md}px`, border: `1px solid ${tk.border}`,
                background: tk.surface3, display: 'grid', placeItems: 'center', overflow: 'hidden', color: tk.text3,
              }}
            >
              {isImage ? (
                <Box
                  component="img"
                  src={query}
                  alt=""
                  onClick={() => openAt(0)}
                  sx={{ display: 'block', maxWidth: '100%', maxHeight: 480, width: 'auto', height: 'auto', objectFit: 'contain', cursor: 'zoom-in' }}
                />
              ) : (
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.3"><rect x="3" y="4" width="18" height="16" rx="3" /><circle cx="9" cy="10" r="2" /><path d="m4 18 5-4 4 3 3-2 4 3" /></svg>
              )}
            </Box>
            {item.summary && (
              <Typography sx={{ mt: '11px', fontSize: 12.5, lineHeight: 1.6, color: tk.text2 }}>{item.summary}</Typography>
            )}
          </ResCard>
        </Stack>

        <Stack>
          <ResCard title="Locations">
            <FieldTable
              rows={[
                ['Continent', loc.continent ?? '—'],
                ['Country', loc.country ?? '—'],
                ['City', loc.city ?? '—'],
                ['Landmark', loc.landmark ?? '—'],
                ['Region type', loc.regionType ?? '—'],
                ...(loc.coordinates?.approximate ? ([['Precision', 'approximate']] as [string, string][]) : []),
              ]}
            />
            {candidates.length > 0 && (
              <Box sx={{ mt: '12px' }}>
                <Sech>Top candidates, ranked</Sech>
                <Chips items={candidates} tone="b" />
              </Box>
            )}
          </ResCard>

          {/*
            Its own card now, separate from Locations — same `MapCard`
            (../ui/MapCard) PersonTemplate/NameScanTemplate use for
            combined_phone/combined_email/combined_name: schematic world map
            by default, "Show live map" for the real Google map, and a "Full
            screen" button opening the same MUI fullScreen Dialog. MapCard
            draws its own "Map" ResCard/border already, so it's rendered
            directly rather than nested inside another card's content.
          */}
          {/*
            MapCard's own ResCard uses `fill` (height: 100%) — inside `Stack`
            (a column flex box whose own height is itself just tracking its
            sibling's natural content, not a hard pixel value), a bare
            `height: 100%` has nothing definite to resolve against and would
            collapse to 0. A fixed pixel height here, same idea as
            PersonTemplate's TILE_HEIGHT tiles, gives it something real to
            fill.
          */}
          <Box sx={{ height: 260 }}>
            {mapSlot ?? (
              <MapCard
                addressCount={scenePoint ? 1 : 0}
                mapPoints={scenePoint ? [scenePoint] : []}
                addresses={loc.country ? [{ value: loc.country }] : []}
              />
            )}
          </Box>

          {hasTexts && (
            <ResCard title="Texts and symbols">
              {texts.visibleTexts?.length > 0 && (<><Sech>Visible text</Sech><Chips items={texts.visibleTexts} tone="b" /></>)}
              {texts.symbolsOrLogos?.length > 0 && (<Box sx={{ mt: '12px' }}><Sech>Symbols and logos</Sech><Chips items={texts.symbolsOrLogos} /></Box>)}
              {texts.uniformsOrFlags?.length > 0 && (<Box sx={{ mt: '12px' }}><Sech>Uniforms or flags</Sech><Chips items={texts.uniformsOrFlags} tone="w" /></Box>)}
            </ResCard>
          )}

          <ResCard title="Objects, scene and time">
            {objects.notableObjects?.length > 0 && (<><Sech>Notable objects</Sech><Chips items={objects.notableObjects} /></>)}
            {objects.vehiclesOrTech?.length > 0 && (<Box sx={{ mt: '12px' }}><Sech>Vehicles and tech</Sech><Chips items={objects.vehiclesOrTech} /></Box>)}
            <Box sx={{ mt: '12px' }}>
              <Sech>Time</Sech>
              <Chips items={[time.estimatedTimeOfDay, time.season, time.estimatedDate || 'date unclear'].filter(Boolean)} />
            </Box>
            {entities.length > 0 && (<Box sx={{ mt: '12px' }}><Sech count={entities.length}>Entities</Sech><Chips items={entities} tone="b" /></Box>)}
          </ResCard>
        </Stack>
      </Cols>

      {(() => {
        const showInferred = hasInferred || hasVericity

        const peopleCard = hasPeople && (
          <ResCard title="People in the image" count={people.length} fill={hasPeople && showInferred}>
            <PeopleCarousel people={people} />
          </ResCard>
        )

        const inferredCard = showInferred && (
          <ResCard title="How the location was inferred" count={evidence.length + reflections.length} fill={hasPeople && showInferred}>
            {hasInferred && (
              <Duo>
                <ValueList rows={[...evidence, ...reflections].slice(0, Math.ceil((evidence.length + reflections.length) / 2)).map((e) => ({ value: e, sources: ['photo_intel'] }))} cap={99} />
                <ValueList rows={[...evidence, ...reflections].slice(Math.ceil((evidence.length + reflections.length) / 2)).map((e) => ({ value: e, sources: ['photo_intel'] }))} cap={99} />
              </Duo>
            )}
            {hasVericity && (
              <Box sx={{ mt: hasInferred ? '12px' : 0 }}>
                <Sech>Vericity score, by section</Sech>
                <Chips items={vericity.map(([k, v]) => `${VERICITY_LABELS[k] || k}: ${v}%`)} tone="b" />
              </Box>
            )}
            <Typography sx={{ fontSize: 10.5, color: tk.text3, mt: '10px' }}>
              Model inferences, not verified facts. Location confidence {pct(item.locationConfidence) ?? '—'}, profile confidence {pct(item.confidence) ?? '—'}.
            </Typography>
          </ResCard>
        )

        // Both present -> share one row, same width each. Only one present ->
        // render it alone, full width, rather than leaving half a row empty.
        if (hasPeople && showInferred) {
          return (
            <Cols n={2}>
              {peopleCard}
              {inferredCard}
            </Cols>
          )
        }
        return (
          <>
            {peopleCard}
            {inferredCard}
          </>
        )
      })()}

      <RawCard data={apiData} />
      {lightbox}
    </>
  )
}
